/**
 * @file TasksDashboard.jsx
 * @description Task List dashboard showing Todoist-style inbox view with inline task creation.
 */

import React, { useState, useEffect, useRef } from "react";
import { getAuth } from "firebase/auth";
import { listTasks, subscribeToTask, createTask, listTaskUsers, subscribeToLabels, migratePriorities, backfillLabels, updateTask } from "../../firebase/tasks";
import { TaskRow } from "../../components/tms/TaskRow";
import { TaskTitleInput } from "../../components/tms/TaskTitleInput";
import { UserSelectDropdown } from "../../components/tms/UserSelectDropdown";
import { DatePicker } from "../../components/tms/DatePicker";
import { LabelTag } from "../../components/tms/LabelTag";
import { RecurrencePicker } from "../../components/tms/RecurrencePicker";
import { UndoToast } from "../../components/tms/UndoToast";
import TaskSearchInput from "../../components/tms/TaskSearchInput";
import { FiX, FiSend } from "react-icons/fi";
import { useTaskRole } from "../../hooks/useTaskRole";

export function TasksDashboard() {
  const { isTaskAdmin } = useTaskRole();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Inline Task Creation form states
  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [newPriority, setNewPriority] = useState("Low");
  const [newStatus, setNewStatus] = useState("Open");
  const [newAssigneeUid, setNewAssigneeUid] = useState("");
  const [newLabels, setNewLabels] = useState([]);
  const [newRecurrence, setNewRecurrence] = useState(null);

  // Undo toast state for recurring task completion
  const [undoToast, setUndoToast] = useState({ visible: false, message: "", taskId: null, previousDueDate: null });

  // Keep track of real-time unsubscribes
  const unsubsRef = useRef([]);

  const clearUnsubs = () => {
    unsubsRef.current.forEach((unsub) => {
      if (typeof unsub === "function") {
        unsub();
      }
    });
    unsubsRef.current = [];
  };

  // Fetch tasks initially and subscribe to updates
  useEffect(() => {
    let active = true;
    let unsubLabels = null;

    const initDashboard = async () => {
      setLoading(true);
      try {
        await migratePriorities().catch(err => console.error("Priority migration error", err));
        if (isTaskAdmin) {
          await backfillLabels().catch(err => console.error("Label backfill error", err));
        }
        const [fetchedTasks, fetchedUsers] = await Promise.all([
          listTasks().catch(err => { console.error("listTasks error", err); return []; }),
          listTaskUsers().catch(err => { console.error("listTaskUsers error", err); return []; }),
        ]);
        
        if (!active) return;
        setUsers(fetchedUsers);

        // Subscribe to labels in real-time — stays in sync across all users and views
        unsubLabels = subscribeToLabels((updatedLabels) => {
          if (active) setAllLabels(updatedLabels);
        });

        const sorted = fetchedTasks.sort((a, b) => b.taskNumber - a.taskNumber);
        setTasks(sorted);
        setLoading(false);

        clearUnsubs();

        const newUnsubs = sorted.map((task) => {
          return subscribeToTask(task.id, (updatedTask) => {
            if (!active) return;
            if (updatedTask) {
              setTasks((prev) =>
                prev.map((pt) => (pt.id === updatedTask.id ? { ...pt, ...updatedTask } : pt))
              );
            } else {
              // Task deleted
              setTasks((prev) => prev.filter((pt) => pt.id !== task.id));
            }
          });
        });

        unsubsRef.current = newUnsubs;
      } catch (err) {
        console.error("Error loading tasks:", err);
        if (active) setLoading(false);
      }
    };

    initDashboard();

    return () => {
      active = false;
      clearUnsubs();
      if (unsubLabels) unsubLabels();
    };
  }, [isTaskAdmin]);

  // Handle Inline Add Task submit
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    const auth = getAuth();
    const currentUser = auth.currentUser;

    const cleanTitle = newTitle.replace(/@\S+/g, '').replace(/\s+/g, ' ').trim();
    const newTaskData = {
      title: cleanTitle,
      description: newDescription.trim(),
      priority: newPriority,
      dueDate: newDueDate || null,
      status: newStatus,
      labels: newLabels,
      recurrence: newRecurrence || null,
      createdByUid: currentUser ? currentUser.uid : "anonymous",
      updatedByUid: currentUser ? currentUser.uid : "anonymous",
      assigneeUid: newAssigneeUid || null,
    };

    try {
      const created = await createTask(newTaskData);
      
      // Update UI list and subscribe to the new task updates
      setTasks((prev) => {
        const updated = [created, ...prev];
        return updated.sort((a, b) => b.taskNumber - a.taskNumber);
      });

      const unsub = subscribeToTask(created.id, (updatedTask) => {
        if (updatedTask) {
          setTasks((prev) =>
            prev.map((pt) => (pt.id === updatedTask.id ? { ...pt, ...updatedTask } : pt))
          );
        } else {
          setTasks((prev) => prev.filter((pt) => pt.id !== created.id));
        }
      });

      unsubsRef.current.push(unsub);

      // Reset form states
      setNewTitle("");
      setNewDescription("");
      setNewDueDate("");
      setNewPriority("Low");
      setNewStatus("Open");
      setNewAssigneeUid("");
      setNewLabels([]);
      setNewRecurrence(null);
      setIsAdding(false);
    } catch (err) {
      console.error("Error creating task:", err);
    }
  };

  const handleAddLabel = (labelName) => {
    setNewLabels(prev => prev.includes(labelName) ? prev : [...prev, labelName]);
    // subscribeToLabels auto-updates allLabels in real-time; no manual refresh needed
  };

  const handleRemoveLabel = (labelName) => {
    setNewLabels(prev => prev.filter(l => l !== labelName));
  };

  const handleRecurrenceComplete = ({ taskId, taskTitle, previousDueDate, nextDueDate }) => {
    setUndoToast({
      visible: true,
      message: `"${taskTitle}" completed — next due ${nextDueDate}`,
      taskId,
      previousDueDate,
    });
  };

  const handleUndoRecurrence = async () => {
    if (!undoToast.taskId || !undoToast.previousDueDate) return;
    try {
      await updateTask(undoToast.taskId, {
        dueDate: undoToast.previousDueDate,
        status: "Open",
      });
    } catch (err) {
      console.error("Error undoing recurrence:", err);
    }
  };

  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [activeTab, setActiveTab] = useState("all");

  // Filter tasks to only show active ones (not Closed) and assigned to the current user
  const assignedTasks = tasks.filter(
    (task) => task.status !== "Closed" && task.assigneeUid === currentUser?.uid
  );
  const recurringTasksCount = assignedTasks.filter((t) => t.recurrence?.isRecurring).length;
  const displayedTasks = activeTab === "recurring"
    ? assignedTasks.filter((t) => t.recurrence?.isRecurring)
    : assignedTasks;

  const derivedAllLabels = Array.from(new Set([
    ...allLabels.map(l => l.name.toLowerCase()),
    ...tasks.flatMap(t => (t.labels || []).map(l => l.toLowerCase()))
  ])).map(name => {
    const existing = allLabels.find(l => l.name.toLowerCase() === name);
    return existing || { id: name, name: name, color: "#6b7280" };
  });

  return (
    <div className="min-h-screen bg-white py-10 px-4 sm:px-6 md:px-8 max-w-3xl mx-auto">
      {/* Title & Search */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <span>Inbox</span>
        </h1>
        <TaskSearchInput />
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab("all")}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors border-none cursor-pointer ${
            activeTab === "all"
              ? "bg-gray-900 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          All ({assignedTasks.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("recurring")}
          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full transition-colors border-none cursor-pointer ${
            activeTab === "recurring"
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <span>🔄</span>
          <span>Repeatable ({recurringTasksCount})</span>
        </button>
      </div>

      {/* Task List Content */}
      {loading ? (
        <ul className="space-y-0 border-t border-gray-100">
          {Array.from({ length: 5 }).map((_, idx) => (
            <li key={idx} className="flex items-start gap-3 py-3 px-4 border-b border-gray-100 animate-pulse">
              <div className="w-5 h-5 rounded-full border-2 border-gray-200 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2 mt-1">
                <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
                <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <ul className="space-y-0 border-t border-gray-100 -mx-4 sm:mx-0">
            {displayedTasks.map((task) => (
              <TaskRow key={task.id} task={task} users={users} allLabels={derivedAllLabels} setAllLabels={setAllLabels} currentUser={currentUser} onRecurrenceComplete={handleRecurrenceComplete} />
            ))}
          </ul>

          {displayedTasks.length === 0 && (
            <div className="py-12 text-center select-none">
              <p className="text-gray-400 text-sm font-medium">
                {activeTab === "recurring" ? "No repeatable tasks found" : "No tasks — add one below"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Inline Create Task Form & Expand Trigger */}
      {!isAdding ? (
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          className="flex justify-center items-center gap-2 text-gray-400 hover:text-red-500 py-3 px-4 w-full transition-all duration-150 font-medium text-sm rounded-lg hover:bg-red-50/40 mt-2 focus:outline-none focus:ring-1 focus:ring-red-200 border-none outline-none bg-transparent"
        >
          <span className="text-red-500 text-lg font-bold flex items-center justify-center w-5 h-5 rounded-full hover:bg-red-100">
            +
          </span>
          <span>Add task</span>
        </button>
      ) : (
        <form
          onSubmit={handleAddTask}
          className="border border-gray-200 rounded-xl p-3 sm:p-4 mt-3 shadow-sm bg-white space-y-3"
        >
          <div className="space-y-1">
            <TaskTitleInput
              value={newTitle}
              onChange={setNewTitle}
              onAddLabel={handleAddLabel}
              allLabels={derivedAllLabels}
              autoFocus={true}
            />
            <textarea
              placeholder="Description"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="w-full text-xs text-gray-500 placeholder-gray-400 border-none focus:ring-0 p-0 resize-none focus:outline-none mt-1 leading-relaxed"
              rows={2}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-50">
            {/* Due Date Input */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Due:</span>
              <DatePicker value={newDueDate} onChange={(val) => setNewDueDate(val || "")} />
            </div>

            {/* Assignee Input */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">
                Assignee<span className="text-red-500">*</span>:
              </span>
              <UserSelectDropdown
                users={users}
                value={newAssigneeUid}
                onChange={setNewAssigneeUid}
              />
            </div>

            {/* Priority Input */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Priority:</span>
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="Low">🟢 Low</option>
                <option value="Medium">🟡 Medium</option>
                <option value="High">🔴 High</option>
              </select>
            </div>
            
            {/* Status Input */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Status:</span>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Closed">Closed</option>
              </select>
            </div>

            {/* Recurrence Input */}
            <div className="flex items-center gap-1">
              <RecurrencePicker value={newRecurrence} onChange={setNewRecurrence} />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {/* Labels */}
            <div className="flex flex-wrap items-center gap-1.5">
              {newLabels && newLabels.length > 0 && newLabels.map((labelName) => {
                const labelColor = derivedAllLabels.find(l => l.name.toLowerCase() === labelName.toLowerCase())?.color;
                return (
                  <LabelTag 
                    key={labelName} 
                    name={labelName} 
                    color={labelColor}
                    onRemove={handleRemoveLabel} 
                  />
                );
              })}
            </div>

            <div className="flex items-center gap-2 ml-auto shrink-0">
              <div className="relative group/cancel">
                <button
                  type="button"
                  onClick={() => {
                    setIsAdding(false);
                    setNewLabels([]);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
                  aria-label="Cancel"
                >
                  <FiX className="w-4 h-4" />
                </button>
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/cancel:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                  Cancel
                </span>
              </div>
              
              <div className="relative group/save">
                <button
                  type="submit"
                  disabled={!newTitle.trim() || !newAssigneeUid}
                  className="w-8 h-8 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed border-none outline-none focus:outline-none cursor-pointer"
                  aria-label="Save"
                >
                  <FiSend className="w-4 h-4" />
                </button>
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/save:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                  Save
                </span>
              </div>
            </div>
          </div>
        </form>
      )}

      <UndoToast
        message={undoToast.message}
        visible={undoToast.visible}
        onUndo={handleUndoRecurrence}
        onDismiss={() => setUndoToast({ visible: false, message: "", taskId: null, previousDueDate: null })}
      />
    </div>
  );
}

export default TasksDashboard;
